import { v4 as uuidv4 } from "uuid";

import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { Cookie } from "@/utils/cookie";
import { polyfillGetRandomValuesIfNecessary } from "@/utils/polyfills";

import { AnalyticsState } from "./analytics-state";
import { AnalyticsData, GASv3 } from "./gasv3";
import { Gtm } from "./gtm-integration";
import { Helpers } from "./helpers";
import { MetricKey, Metrics } from "./metrics";
import { Tracking } from "./tracking";

/**
 * Module for initializing the wrapper.
 * @module Initializer
 */
export class Initializer {
    /**
     * Loads and initializes segment.io.
     * @function initWithPageAnalytics
     * @param {string} writeKey - required, Segment.io key
     * @param {object} pageViewProperties - optional, dictionary of properties of the page (see: https://segment.com/docs/libraries/analytics.js/#page)
     * @param {object} loadOptions - optional, object with a dictionary of which integrations to load (see: https://segment.com/docs/sources/website/analytics.js/#load-options)
     * @param {number} loadTimestamp - optional, the timestamp at which the initializing site loaded this script
     * @param {string} anonymousId - GASv3 analytics client anonymous id
     * @param {function} createAnalyticsEvent - analytics next CreateUIAnalyticsEvent
     */
    public static initWithPageAnalytics(
        writeKey: string,
        pageViewProperties: AnalyticsData,
        loadOptions: object,
        loadTimestamp: number,
        anonymousId: string,
        createAnalyticsEvent: CreateUIAnalyticsEvent | null,
        analyticsOptOut: boolean
    ) {
        AnalyticsState.anonymousId = anonymousId;
        AnalyticsState.analyticsOptOut = analyticsOptOut;

        // Always set createAnalyticsEvent so operational events can fire when opt-out
        AnalyticsState.createAnalyticsEvent = createAnalyticsEvent;

        // set load timestamp for calculating metrics
        Metrics.setLoadTimestamp(loadTimestamp);

        if (!analyticsOptOut) {
            // TODO: Switch these params to dynamically config based on writeKey
            Gtm.initialize(writeKey);
        }

        Initializer.captureUTMs();

        // set unpopulated params to empty objects
        if (typeof loadOptions !== "object" || loadOptions === null) {
            loadOptions = {};
        }

        if (
            typeof pageViewProperties !== "object" ||
            pageViewProperties === null
        ) {
            pageViewProperties = {};
        }

        // adding marketing boolean if current product comes under marketing domain
        Initializer.setIsMarketingEvent(pageViewProperties);

        Initializer.setSnIdAndAtlFirstVisit(pageViewProperties);

        // store params for later reporting if necessary.
        AnalyticsState.writeKey = writeKey;

        // TODO: pageViewOptions used to be passed in, but doesn't appear to be used outside of Segment.
        // Can we just delete this completely?
        AnalyticsState.pageViewOptions = {};

        // auto-fire page view
        Helpers.addActualUrl(pageViewProperties);

        Tracking.pageView(pageViewProperties);

        if (!analyticsOptOut) {
            // add auto tracking and configure google analytics integration
            Initializer.autoTrack();
            Initializer.gaIntegration();

            Initializer.globalCallReady();
        }
    }

    /**
     * Set SN_ID in the cookie
     */
    public static setSnIdAndAtlFirstVisit(
        pageViewProperties: Record<string, unknown>
    ) {
        // Get SN_ID from cookie, if doesn't exist make one
        const SN_ID_COOKIE_KEY = "sn_id";
        let SN_ID_COOKIE_VALUE = Cookie.readCookie(SN_ID_COOKIE_KEY) ?? "";
        if (SN_ID_COOKIE_VALUE) {
            SN_ID_COOKIE_VALUE = decodeURIComponent(SN_ID_COOKIE_VALUE).replace(
                /['"]+/g,
                ""
            );
            // if sn_id cookie does exist, we verify to make sure the atl_first_visit local storage item is also present and not expired
            if (Initializer.isSetAtlFirstVisit()) {
                Initializer.setAtlFirstVisitInLocalStorage(pageViewProperties);
            }
        } else {
            polyfillGetRandomValuesIfNecessary();
            SN_ID_COOKIE_VALUE = uuidv4();
            Cookie.writeCookie(SN_ID_COOKIE_KEY, SN_ID_COOKIE_VALUE, 1, true);
            // if sn_id cookie doesn't exist or has expired, then we'll just create a new atl_first_visit locat storage item
            Initializer.setAtlFirstVisitInLocalStorage(pageViewProperties);
        }
    }

    /**
     * Check if atl_first_visit is missing and or has expired then we return true, else false
     * @returns boolean
     */
    public static isSetAtlFirstVisit() {
        const atl_first_visit_data = localStorage.getItem("atl_first_visit");
        if (atl_first_visit_data) {
            const parsedAtlFirstVisitData = JSON.parse(atl_first_visit_data);
            const expiresAt = parsedAtlFirstVisitData?.expiresAt;
            if (expiresAt == null) {
                return true;
            }
            if (new Date(expiresAt).getTime() < Date.now()) {
                return true;
            }
            return false;
        }
        return true;
    }

    /**
     * Set the atl_first_visit data in localStorage
     * @param pageViewProperties
     */
    public static setAtlFirstVisitInLocalStorage(
        pageViewProperties: Record<string, unknown>
    ) {
        const referrer = document.referrer || "about:blank";
        const refUrl = new URL(referrer);
        const atl_first_visit_data = Initializer.buildAtlFirstVisitData(
            pageViewProperties,
            refUrl
        );

        try {
            localStorage.setItem(
                "atl_first_visit",
                JSON.stringify(atl_first_visit_data)
            );
        } catch (e) {
            Helpers.sendWrapperLoadError(
                "atl_first_visit localStorage update failed",
                JSON.stringify(e)
            );
        }
    }

    /**
     * Build the atl_first_visit data
     * @param pageViewProperties
     * @param refUrl
     * @param expiry
     * @returns
     */
    private static buildAtlFirstVisitData(
        pageViewProperties: Record<string, unknown>,
        refUrl: URL
    ) {
        const expiry = 24 * 60 * 60 * 1000; // 24 hours
        const orEmpty = (v: unknown) =>
            v != null && v !== "" ? String(v) : "";
        const orUnknown = (v: unknown) =>
            v != null && v !== "" ? String(v) : "unknown";
        const searchLogicEntries = JSON.stringify(
            Object.fromEntries(
                new URLSearchParams(window.location.search ?? "").entries()
            )
        );

        return {
            "originProduct": pageViewProperties["originProduct"] ?? "",
            "campaign":
                AnalyticsState.utms ?? Initializer.captureAtlFirstVisitUTMs(),
            "page_url_params_json": searchLogicEntries,
            "page_url": orEmpty(window.location.href),
            "page_canonical_url": orEmpty(
                Helpers.maskURL(Helpers.getCanonicalUrl())
            ),
            "page_path": orEmpty(window.location.pathname),
            "page_domain": orEmpty(window.location.hostname),
            "page_subdomain": orEmpty(window.location.hostname),
            "page_referrer_url": orEmpty(document.referrer),
            "page_referrer_domain": orEmpty(refUrl.hostname),
            "page_referrer_subdomain": orEmpty(refUrl.hostname),
            "page_url_parameters": orEmpty(window.location.search),
            "page_referrer_url_parameters": orEmpty(refUrl.search),
            "isMarketingEvent": GASv3.isMarketingEvent ?? false,
            "cookiesEnabled": orUnknown(window.navigator.cookieEnabled),
            "activeGroups": orEmpty(window.OnetrustActiveGroups),
            "onboardingJourney": orEmpty(
                localStorage.getItem("onboarding-journey")
            ),
            "cmsPageVariant": orEmpty(window.cmsPageVariant),
            "bxp-gateway-id": orEmpty(
                Cookie.readCookie("bxp_gateway_request_id")
            ),
            "user-agent": orEmpty(window.navigator.userAgent),
            "expiresAt": new Date(Date.now() + expiry).toISOString(),
            "onboarding-journey": orEmpty(
                localStorage.getItem("onboarding-journey")
            )
        };
    }

    /**
     * Calls window functions that are awaiting a wrapper ready state
     * @function globalCallReady
     */
    public static globalCallReady() {
        if (typeof window.analyticsPageReady === "function") {
            window.analyticsPageReady();
        }

        Metrics.logMetric(MetricKey.TIME_TO_READY);
        Metrics.sendMetrics();
    }

    public static captureAtlFirstVisitUTMs() {
        return {
            "utm_sfdc-campaign_id": "",
            "utm_campaign": "",
            "utm_medium": "",
            "utm_source": "",
            "utm_content": "",
            "utm_term": ""
        };
    }

    public static captureUTMs() {
        const utmKeys = Initializer.captureAtlFirstVisitUTMs();

        AnalyticsState.utms = Helpers.getAndSetUtms(utmKeys);
    }

    /**
     * Applies auto-tracking for select domains
     * @function autoTrack
     */
    public static autoTrack() {
        Tracking.autoTrackButtons(true);
        Tracking.autoTrackLinks(true);
        Tracking.autoTrackVideos();
        Tracking.autoTrackSelects();
        Tracking.autoTrackTabs();
        Tracking.autoTrackDetails();
    }

    /**
     * Completes path overrides for Google Analytics
     * @function gaIntegration
     */
    public static gaIntegration() {
        const emailDetected = Helpers.emailAddressIsDetected();
        const pathOverride = "/path-contained-email-address";

        if (typeof window.ga !== "undefined") {
            window.ga("require", "linker");
            window.ga("linker:autoLink", [
                "atlassian.com",
                "bitbucket.org",
                "hipchat.com",
                "attendease.com",
                "get.atlassian.com",
                "eegeventsite.secure.force.com",
                "atlassian.swoogo.com"
            ]);

            if (emailDetected) {
                // only override what is needed
                if (Helpers.urlContainsEmail(window.location.href)) {
                    window.ga(
                        "set",
                        "location",
                        window.location.origin + pathOverride
                    );
                    window.ga("set", "path", pathOverride);
                }
                if (Helpers.urlContainsEmail(document.referrer)) {
                    window.ga(
                        "set",
                        "referrer",
                        Helpers.hostnameFromUrl(document.referrer) +
                            pathOverride
                    );
                }
            }
        }
    }

    public static setIsMarketingEvent(
        pageViewProperties: Record<string, unknown>
    ) {
        if (typeof pageViewProperties["isMarketingEvent"] !== "boolean") {
            GASv3.isMarketingEvent = true;
        } else {
            GASv3.isMarketingEvent = pageViewProperties["isMarketingEvent"];
        }
    }

    public static setXID(xid: number) {
        Tracking.xid = xid;
    }
}
